// ========================================
// QUERY CLIENT CONFIGURATION
// React Query setup with sensible defaults
// Updated with Unit Types, Units, Plans, Amenities
// ========================================

import { QueryClient } from '@tanstack/react-query';

// Create query client with optimized defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is fresh for 30 seconds
      staleTime: 30 * 1000,
      
      // Cache data for 5 minutes
      gcTime: 5 * 60 * 1000,
      
      // Retry failed requests 1 time
      retry: 1,
      
      // Don't refetch on window focus in development
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      
      // Don't refetch on reconnect automatically
      refetchOnReconnect: 'always',
      
      // Show stale data while revalidating
      keepPreviousData: true,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// ========================================
// QUERY KEYS
// Centralized query key management
// ========================================
export const queryKeys = {
  // Assets
  assets: {
    all: ['assets'],
    list: () => [...queryKeys.assets.all, 'list'],
    detail: (id) => [...queryKeys.assets.all, 'detail', id],
  },
  
  // Clients
  clients: {
    all: ['clients'],
    list: (filters) => [...queryKeys.clients.all, 'list', filters],
    detail: (id) => [...queryKeys.clients.all, 'detail', id],
    pipelineStats: () => [...queryKeys.clients.all, 'pipeline-stats'],
  },
  
  // PLRs
  plrs: {
    all: ['plrs'],
    list: () => [...queryKeys.plrs.all, 'list'],
    detail: (id) => [...queryKeys.plrs.all, 'detail', id],
    leads: (id) => [...queryKeys.plrs.all, 'leads', id],
    sections: (id) => [...queryKeys.plrs.all, 'sections', id],
  },
  
  // Regions
  regions: {
    all: ['regions'],
    list: () => [...queryKeys.regions.all, 'list'],
    detail: (id) => [...queryKeys.regions.all, 'detail', id],
  },
  
  // Users
  users: {
    all: ['users'],
    list: () => [...queryKeys.users.all, 'list'],
  },
  
  // Spaces (Office/Warehousing)
  spaces: {
    all: ['spaces'],
    byBuilding: (buildingId) => [...queryKeys.spaces.all, 'building', buildingId],
    detail: (id) => [...queryKeys.spaces.all, 'detail', id],
  },
  
  // Email Campaigns
  emails: {
    all: ['emails'],
    list: () => [...queryKeys.emails.all, 'list'],
    detail: (id) => [...queryKeys.emails.all, 'detail', id],
  },
  
  // ========================================
  // NEW: Unit Types (Residential)
  // ========================================
  unitTypes: {
    all: ['unitTypes'],
    byAsset: (assetId) => [...queryKeys.unitTypes.all, 'asset', assetId],
    detail: (id) => [...queryKeys.unitTypes.all, 'detail', id],
    premiums: (unitTypeId) => [...queryKeys.unitTypes.all, 'premiums', unitTypeId],
  },

  // ========================================
  // NEW: Units (Residential Individual Units)
  // ========================================
  units: {
    all: ['units'],
    byAsset: (assetId, filters) => [...queryKeys.units.all, 'asset', assetId, filters],
    byBuilding: (buildingId) => [...queryKeys.units.all, 'building', buildingId],
    detail: (id) => [...queryKeys.units.all, 'detail', id],
    matrix: (assetId) => [...queryKeys.units.all, 'matrix', assetId],
  },

  // ========================================
  // NEW: Plans & Limits
  // ========================================
  plans: {
    all: ['plans'],
    list: () => [...queryKeys.plans.all, 'list'],
    current: () => [...queryKeys.plans.all, 'current'],
    detail: (id) => [...queryKeys.plans.all, 'detail', id],
    usage: () => [...queryKeys.plans.all, 'usage'],
    checkLimit: (resourceType) => [...queryKeys.plans.all, 'check', resourceType],
  },

  // ========================================
  // NEW: Amenities (Residential)
  // ========================================
  amenities: {
    all: ['amenities'],
    list: (category) => [...queryKeys.amenities.all, 'list', category],
    byAsset: (assetId) => [...queryKeys.amenities.all, 'asset', assetId],
  },

  // ========================================
  // NEW: Virtual Tours
  // ========================================
  virtualTours: {
    all: ['virtualTours'],
    byAsset: (assetId) => [...queryKeys.virtualTours.all, 'asset', assetId],
    detail: (tourId) => [...queryKeys.virtualTours.all, 'detail', tourId],
    public: (assetId) => [...queryKeys.virtualTours.all, 'public', assetId],
  },

  // ========================================
  // NEW: Agent (AI Sales Agent)
  // ========================================
  agent: {
    conversations: {
      all: ['agent-conversations'],
      list: (filters) => ['agent-conversations', 'list', filters],
      detail: (id) => ['agent-conversations', 'detail', id],
    },
    kb: {
      roles: (assetId) => ['agent-kb-roles', assetId],
      documents: (assetId) => ['agent-kb-documents', assetId],
      qaPairs: (assetId, filters) => ['agent-kb-qa', assetId, filters],
      stats: (assetId) => ['agent-kb-stats', assetId],
      verifiers: (assetId) => ['agent-kb-verifiers', assetId],
    },
    config: {
      get: (assetId) => ['agent-config', assetId],
      health: () => ['agent-health'],
    },
  },
};

export default queryClient;